import { useEffect, useState } from 'react';
import { Modal, Form, Radio, Space, Button, Tag, Typography, Empty, App } from 'antd';
import { useLanguages } from '@/hooks/useLanguages';
import { useStartTranslate } from '@/hooks/useTranslateBatch';
import { useTranslateJobsStore } from '@/stores/translateJobsStore';
import { langHasContent, type TopicDetails } from '@/types/topic';
import { AiTranslateProgress } from './AiTranslateProgress';

type OverwritePolicy = 'missing-only' | 'all';

interface Props {
  open: boolean;
  topicId: string;
  /** The topic's current details — used to detect which languages are missing. */
  details?: TopicDetails;
  /** Languages to pre-check when the modal opens (e.g. just the active one). */
  defaultTargets?: string[];
  /** Called as each job finishes, so the editor can reload fresh translations. */
  onTranslated?: () => void;
  onClose: () => void;
}

/**
 * Translates the CURRENT topic into one or more selected target languages —
 * one translate-batch job per selected language, scoped to this topic. English
 * is the source and excluded; bundled zh-Hans is an eligible target (topic
 * content may be machine-translated into it).
 */
export function TranslateAllModal({ open, topicId, details, defaultTargets, onTranslated, onClose }: Props) {
  const { message } = App.useApp();
  const langsQ = useLanguages();
  const startMut = useStartTranslate();
  const trackJob = useTranslateJobsStore((s) => s.track);

  const [overwrite, setOverwrite] = useState<OverwritePolicy>('missing-only');
  const [selected, setSelected] = useState<string[]>([]);
  const [started, setStarted] = useState<{ lang: string; jobId: string }[] | null>(null);

  // Every enabled language except the source (English).
  const targets = (langsQ.data ?? []).filter((l) => l.enabled && l.code !== 'en');
  const targetCodes = targets.map((l) => l.code);

  // Re-seed the selection each time the modal opens: the caller's
  // defaultTargets (constrained to eligible), else every eligible language.
  useEffect(() => {
    if (!open) return;
    const seed = defaultTargets?.filter((c) => targetCodes.includes(c));
    setSelected(seed && seed.length ? seed : targetCodes);
    setStarted(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTargets, langsQ.data]);

  const toggle = (code: string, on: boolean) =>
    setSelected((s) => (on ? [...new Set([...s, code])] : s.filter((c) => c !== code)));

  const selectAll = () => setSelected(targetCodes);
  const clear = () => setSelected([]);
  const selectMissing = () =>
    setSelected(targetCodes.filter((c) => !details || !langHasContent(details, c)));

  async function start() {
    if (selected.length === 0) { message.warning('Select at least one language'); return; }
    try {
      const jobs = await Promise.all(
        targets
          .filter((l) => selected.includes(l.code))
          .map(async (l) => {
            const r = await startMut.mutateAsync({
              target_lang: l.code,
              source_lang: 'en',
              scope: 'topics',
              overwrite_policy: overwrite,
              topic_ids: [topicId],
            });
            trackJob(r.job_id);
            return { lang: l.code, jobId: r.job_id };
          }),
      );
      setStarted(jobs);
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  function reset() {
    setStarted(null);
    onClose();
  }

  return (
    <Modal open={open} title="Translate topic" onCancel={reset} footer={null} width={720} destroyOnHidden>
      {started ? (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {started.map((j) => (
            <div key={j.lang}>
              <Typography.Text strong>{j.lang}</Typography.Text>
              <AiTranslateProgress jobId={j.jobId} onTerminal={(s) => { if (s !== 'failed') onTranslated?.(); }} />
            </div>
          ))}
          <div style={{ textAlign: 'right' }}>
            <Button onClick={reset}>Close (jobs continue in background)</Button>
          </div>
        </Space>
      ) : (
        <Form layout="vertical">
          <Form.Item
            label={
              <Space>
                <span>Target languages</span>
                <Button size="small" type="link" onClick={selectAll}>All</Button>
                <Button size="small" type="link" onClick={selectMissing}>Only missing</Button>
                <Button size="small" type="link" onClick={clear}>Clear</Button>
              </Space>
            }
          >
            {targets.length > 0 ? (
              <Space wrap size={[8, 8]}>
                {targets.map((l) => (
                  <Tag.CheckableTag
                    key={l.code}
                    checked={selected.includes(l.code)}
                    onChange={(on) => toggle(l.code, on)}
                    style={{ border: '1px solid #d9d9d9', padding: '2px 10px', userSelect: 'none' }}
                  >
                    {l.code} — {l.name_native}
                    {details && !langHasContent(details, l.code) ? ' ○' : ''}
                  </Tag.CheckableTag>
                ))}
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No eligible target languages" />
            )}
          </Form.Item>
          <Form.Item label="Overwrite policy">
            <Radio.Group value={overwrite} onChange={(e) => setOverwrite(e.target.value)}>
              <Radio value="missing-only">Missing only (preserve existing translations)</Radio>
              <Radio value="all">Overwrite all</Radio>
            </Radio.Group>
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            English is the source language and is excluded. ○ marks a language with no content yet.
          </Typography.Paragraph>
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={reset}>Cancel</Button>
              <Button type="primary" onClick={start} loading={startMut.isPending} disabled={selected.length === 0}>
                Translate {selected.length} language{selected.length === 1 ? '' : 's'}
              </Button>
            </Space>
          </div>
        </Form>
      )}
    </Modal>
  );
}
